import type { AnalyticsProvider, AnalyticsEventName, AnalyticsPayload } from '../types';
import { env } from '@/config/env';
import { getEffectiveParam } from '@/features/config';
import { getDeviceType } from '@/shared/lib/native-browser';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Resolved at init time: the funnel config's `gg` param (priority 0) wins over
 * the build-time env id, so a config can retarget GA without a redeploy.
 */
let gaId = '';

/**
 * Explicit mapping of the analytics payload to GA4 event params. Only keys that
 * actually have a value are emitted — plain screen events (e.g.
 * `Settl_Quiz_Loaded`) carry just `timestamp`/`device_type` (+ campaign
 * when attributed); the ecommerce fields appear only on checkout/purchase
 * events that pass them. Nothing empty is sent.
 */
function toGaEventParams(payload: AnalyticsPayload): Record<string, unknown> {
  const params: Record<string, unknown> = {
    timestamp: Date.now(),
    device_type: getDeviceType(),
    campaign_id: payload.attribution?.campaign_id ?? undefined,
    campaign_name: payload.attribution?.campaign_name ?? undefined,
    product_id: payload.productId,
    currency: payload.currency,
    payment_method: payload.paymentMethod,
    value: payload.price,
    transaction_id: payload.transactionId,
    answer: payload.answer,
    ...payload.metadata,
  };

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

export const gaProvider: AnalyticsProvider = {
  name: 'google-analytics',

  init() {
    gaId = getEffectiveParam('gg')?.trim() || env.VITE_GA_MEASUREMENT_ID || '';
    if (!gaId) return;
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    // gtag.js only processes `arguments` objects pushed to the dataLayer, not
    // plain arrays — pushing an array silently no-ops, so config/events never
    // reach Google. Use Google's exact snippet form.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', gaId);
  },

  track(event: AnalyticsEventName, payload: AnalyticsPayload) {
    if (!gaId) return;
    window.gtag('event', event, toGaEventParams(payload));
  },

  page(path: string) {
    if (!gaId) return;
    window.gtag('event', 'page_view', { page_path: path });
  },
};
